export enum CardCategoryEnum {
  Credit = "credit",
  Debit = "debit",
  Prepaid = "prepaid",
  DeferredDebit = "deferred_debit",
  Charge = "charge",
  Unknown = "unknown",
}

export enum AchAccountTypeEnum {
  Checking = "Checking",
  Savings = "Savings",
}

export type AchAccountType = `${AchAccountTypeEnum}`

export enum AchAccountHolderTypeEnum {
  Personal = "Personal",
  Business = "Business",
}

export type AchAccountHolderType = `${AchAccountHolderTypeEnum}`

export const ACH_ACCOUNT_TYPE_OPTIONS = [
  { value: AchAccountTypeEnum.Checking, label: "Checking" },
  { value: AchAccountTypeEnum.Savings, label: "Savings" },
] as const

export const ACH_ACCOUNT_HOLDER_TYPE_OPTIONS = [
  { value: AchAccountHolderTypeEnum.Personal, label: "Personal" },
  { value: AchAccountHolderTypeEnum.Business, label: "Business" },
] as const

export function normalizeAchAccountType(value: string | null | undefined): AchAccountType | null {
  switch ((value ?? "").trim().toLowerCase()) {
    case "checking":
      return AchAccountTypeEnum.Checking
    case "savings":
      return AchAccountTypeEnum.Savings
    default:
      return null
  }
}

export function normalizeAchAccountHolderType(value: string | null | undefined): AchAccountHolderType | null {
  switch ((value ?? "").trim().toLowerCase()) {
    case "personal":
      return AchAccountHolderTypeEnum.Personal
    case "business":
      return AchAccountHolderTypeEnum.Business
    default:
      return null
  }
}

export type CardCategory = `${CardCategoryEnum}`

export const UNSUPPORTED_UNKNOWN_CARD_MESSAGE =
  "This card is currently unsupported, please try a new card or use ACH payment.  If you have any questions, please reach out to your CloudFit point of contact."

export function normalizeCardCategoryKnown(value: string | null | undefined): CardCategory | null {
  const normalized = (value ?? "").trim().toLowerCase()
  if (
    normalized === CardCategoryEnum.Credit ||
    normalized === CardCategoryEnum.Debit ||
    normalized === CardCategoryEnum.Prepaid ||
    normalized === CardCategoryEnum.DeferredDebit ||
    normalized === CardCategoryEnum.Charge ||
    normalized === CardCategoryEnum.Unknown
  ) {
    return normalized
  }

  return null
}

export function normalizeCardCategoryLoose(value: string | null | undefined) {
  const normalized = (value ?? "").trim().toLowerCase()
  return normalized.length > 0 ? normalized : null
}

export function isUnknownCard(value: string | null | undefined) {
  return (value ?? "").trim().toLowerCase() === CardCategoryEnum.Unknown
}

export function isCreditCard(value: string | null | undefined) {
  return (value ?? "").trim().toLowerCase() === CardCategoryEnum.Credit
}

export function parseAllowUnknownCardTypeProcessing(value: unknown) {
  if (typeof value === "boolean") {
    return value
  }

  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true"
  }

  if (typeof value === "number") {
    return value !== 0
  }

  return true
}

export function parsePositiveSurchargePercent(value: number | null | undefined) {
  return value !== null && value !== undefined && Number.isFinite(value) && value > 0 ? value : null
}

export function toDollarsFromCents(amountCents: number) {
  return amountCents / 100
}

export function roundHalfToEven(value: number) {
  const sign = value < 0 ? -1 : 1
  const absoluteValue = Math.abs(value)
  const floor = Math.floor(absoluteValue)
  const fraction = absoluteValue - floor
  const tolerance = Number.EPSILON * 16

  let roundedAbsoluteValue: number
  if (Math.abs(fraction - 0.5) <= tolerance) {
    roundedAbsoluteValue = floor % 2 === 0 ? floor : floor + 1
  } else {
    roundedAbsoluteValue = Math.round(absoluteValue)
  }

  return roundedAbsoluteValue * sign
}

export function calculateSurchargeAmountCents(baseAmountCents: number, surchargePercent: number | null) {
  if (!Number.isFinite(baseAmountCents) || baseAmountCents <= 0) {
    return 0
  }

  if (surchargePercent === null || !Number.isFinite(surchargePercent) || surchargePercent <= 0) {
    return 0
  }

  return roundHalfToEven((baseAmountCents * surchargePercent) / 100)
}